import React, { useState, useEffect } from "react";
import { Paper, Snackbar, Backdrop, CircularProgress } from "@mui/material";
import DataTable from "../components/DataTable";
import FiltersPanel from "../components/FiltersPanel";
import PublishDialog from "../components/PublishDialog";
import { getCurrentWeekDates } from "../utils/dateUtils";
import { renderTruncatedWithTooltip } from "../utils/renderUtils";
import {
  loadScheduleData,
  loadLocations,
  loadRooms,
  loadLines,
} from "../api/scheduleApi";
import { downloadProdSchedule } from "../utils/pdfGenerator";
import { useAuth } from "../auth/useAuth";
import { publishSchedule } from "../api/publishApi";
import { useUser } from "../context/UserContext";

const SchedulePage = () => {
  const { getAccessToken } = useAuth();
  const user = useUser(); // ✅ global user (role + location)
  const [rows, setRows] = useState([]);
  const [locations, setLocations] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [lines, setLines] = useState([]);
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [snackbar, setSnackbar] = useState("");
  const [openPublishDialog, setOpenPublishDialog] = useState(false);
  const [nextRevision, setNextRevision] = useState(null);

  // ----------------------------
  // Default week start/end
  // ----------------------------
  useEffect(() => {
    const { formattedStart, formattedEnd } = getCurrentWeekDates();
    setFilters((prev) => ({
      ...prev,
      startDate: formattedStart,
      endDate: formattedEnd,
    }));
  }, []);

  // ----------------------------
  // Load Locations
  // ----------------------------
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const token = await getAccessToken();
        const data = await loadLocations(token);
        setLocations(data);
      } catch (err) {
        console.error("❌ Failed to load locations:", err);
      }
    };
    fetchLocations();
  }, []);

  // ----------------------------
  // Load Rooms & Lines when Location changes
  // ----------------------------
  useEffect(() => {
    const fetchRoomsAndLines = async () => {
      if (!filters.location) {
        setRooms([]);
        setLines([]);
        return;
      }

      try {
        const token = await getAccessToken();
        const res = await loadRooms(filters.location, token);
        setRooms(res.rooms || []);
        setLines(res.lines || []);
      } catch (error) {
        console.error("❌ Failed to load rooms/lines:", error);
        setRooms([]);
        setLines([]);
      }
    };

    fetchRoomsAndLines();
  }, [filters.location]);

  // ----------------------------
  // Load Schedule Data
  // ----------------------------
  const handleLoad = async () => {
    try {
      setLoading(true);
      const token = await getAccessToken();
      const data = await loadScheduleData(filters, token);
      setRows(data);
    } catch (err) {
      console.error("❌ Failed to load schedule data:", err);
      setSnackbar("❌ Failed to load schedule data");
    } finally {
      setLoading(false);
    }
  };

  // ----------------------------
  // Fetch next revision number
  // ----------------------------
  const fetchNextRevision = async () => {
    try {
      const token = await getAccessToken();
      const res = await fetch(
        `${process.env.REACT_APP_PROD_SCHEDULE_API_URL}/schedules/latest_revision?location=${filters.location}&week_start=${filters.startDate}&week_end=${filters.endDate}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      return (data.latest_revision || 0) + 1;
    } catch (err) {
      console.error("❌ Failed to fetch next revision:", err);
      return 1;
    }
  };

  // ----------------------------
  // Open Publish Dialog
  // ----------------------------
  const handleOpenPublishDialog = async () => {
    if (!rows.length || !filters.location) {
      setSnackbar("⚠️ Please select a location and load data first.");
      return;
    }

    const rev = await fetchNextRevision();
    setNextRevision(rev);
    setOpenPublishDialog(true);
  };

  // ----------------------------
  // Confirm Publish
  // ----------------------------
  const handleConfirmPublish = async (notes) => {
    setOpenPublishDialog(false);
    setPublishing(true);
    try {
      const token = await getAccessToken();

      // Generate PDF (return doc object)
      const { doc } = downloadProdSchedule(
        rows,
        filters.location,
        "",
        filters.startDate,
        filters.endDate,
        { returnDoc: true, revision: nextRevision } // ✅ include revision
      );

      // Convert to base64
      const pdfBlob = doc.output("blob");
      const pdfBase64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(pdfBlob);
      });

      // Payload
      const payload = {
        location: filters.location,
        week_start: filters.startDate,
        week_end: filters.endDate,
        pdf_base64: pdfBase64.split(",")[1],
        notes,
        revision: nextRevision,
        published_by: user?.email || "unknown@msiexpress.com",
      };

      const res = await publishSchedule(payload, token);
      setSnackbar(`✅ Published revision ${res.revision} successfully.`);
    } catch (err) {
      console.error("❌ Publish failed:", err);
      setSnackbar("❌ Failed to publish schedule");
    } finally {
      setPublishing(false);
    }
  };

  // ----------------------------
  // Table Columns
  // ----------------------------
  const columns = [
    { field: "line_name", headerName: "Line Name", width: 150 },
    {
      field: "work_order_id",
      headerName: "Work Order ID",
      width: 150,
      renderCell: (params) => renderTruncatedWithTooltip(params.value),
    },
    { field: "work_order_code", headerName: "Work Order Item Code", width: 170 },
    { field: "work_order_description", headerName: "Work Order Item Description", width: 180 },
    { field: "work_order_reference", headerName: "Work Order Reference", width: 180 },
    { field: "plant_location", headerName: "Plant Location", width: 150 },
    { field: "job_number", headerName: "Job Number", width: 120 },
    { field: "notes", headerName: "Notes", width: 120 },
    { field: "bulk_item_code", headerName: "Bulk Item Code", width: 160 },
    { field: "bulk_item_description", headerName: "Bulk Description", width: 200 },
    { field: "bulk_item_reference", headerName: "Bulk Ref", width: 120 },
    { field: "bulk_item_work_order", headerName: "Bulk Work Order", width: 120 },
    { field: "Mon", headerName: "Monday", width: 110 },
    { field: "Tue", headerName: "Tuesday", width: 110 },
    { field: "Wed", headerName: "Wednesday", width: 110 },
    { field: "Thu", headerName: "Thursday", width: 110 },
    { field: "Fri", headerName: "Friday", width: 110 },
    { field: "Sat", headerName: "Saturday", width: 110 },
    { field: "Sun", headerName: "Sunday", width: 110 },
    { field: "total", headerName: "Total", width: 110 },
    { field: "people", headerName: "People", width: 110 },
  ];

  const handleDownload = () => {
    if (!filters.location || !rows.length) {
      setSnackbar("⚠️ Please load schedule before downloading PDF");
      return;
    }
    downloadProdSchedule(rows, filters.location, "", filters.startDate, filters.endDate);
  };

  return (
    <Paper
      sx={{
        p: 3,
        borderRadius: 3,
        height: "calc(100vh - 130px)",
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
      elevation={3}
    >
      <FiltersPanel
        locations={locations}
        rooms={rooms}
        lines={lines}
        filters={filters}
        setFilters={setFilters}
        loading={loading}
        onLoad={handleLoad}
        onDownload={handleDownload}
        onPublish={handleOpenPublishDialog} // ✅ replaced old handlePublish
        publishing={publishing}
        userRole={user?.role}
        userLocation={user?.location}
      />

      <DataTable rows={rows} columns={columns} loading={loading} />

      {/* ✅ Publish Dialog */}
      <PublishDialog
        open={openPublishDialog}
        onClose={() => setOpenPublishDialog(false)}
        onConfirm={handleConfirmPublish}
        revision={nextRevision}
        loading={publishing}
      />

      {/* Snackbar Feedback */}
      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar("")}
        message={snackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />

      {/* Global overlay for publish */}
      <Backdrop open={publishing} sx={{ color: "#fff", zIndex: 2000 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Paper>
  );
};

export default SchedulePage;
